import * as THREE from 'three'
import { useMemo, useState, useRef } from 'react'
import { createPortal, useFrame } from '@react-three/fiber'
import { useFBO } from '@react-three/drei'
import { ATTRACTOR } from './attractors'
import './shaders/simulationMaterial'
import './shaders/dofPointsMaterial'

export function Particles({
  fov,
  aperture,
  focus,
  attractor = ATTRACTOR.LORENZ,
  transitionDuration = 1.25,
  size = 256,
  ...props
}) {
  const simRef = useRef()
  const renderRef = useRef()
  // Set up FBO
  const [scene] = useState(() => new THREE.Scene())
  const [camera] = useState(() => new THREE.OrthographicCamera(-1, 1, 1, -1, 1 / Math.pow(2, 53), 1))
  const [positions] = useState(() => new Float32Array([-1, -1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0, 1, 1, 0, -1, 1, 0]))
  const [uvs] = useState(() => new Float32Array([0, 1, 1, 1, 1, 0, 0, 1, 1, 0, 0, 0]))
  const targetOptions = {
    minFilter: THREE.NearestFilter,
    magFilter: THREE.NearestFilter,
    format: THREE.RGBAFormat,
    type: THREE.FloatType
  }

  const targetA = useFBO(size, size, targetOptions)
  const targetB = useFBO(size, size, targetOptions)
  const simulationFrame = useRef(0)
  const attractorTransition = useRef({
    from: attractor,
    to: attractor,
    queued: attractor,
    progress: 1
  })
  // Normalize points
  const particles = useMemo(() => {
    const length = size * size
    const particles = new Float32Array(length * 3)
    for (let i = 0; i < length; i++) {
      let i3 = i * 3
      particles[i3 + 0] = (i % size) / size
      particles[i3 + 1] = i / size / size
    }
    return particles
  }, [size])
  // Update FBO and pointcloud every frame
  useFrame((state, delta) => {
    if (!simRef.current || !renderRef.current) return

    const writeTarget =
      simulationFrame.current % 2 === 0 ? targetA : targetB

    const readTarget =
      simulationFrame.current % 2 === 0 ? targetB : targetA

    if (simulationFrame.current > 0) {
      simRef.current.uniforms.positions.value = readTarget.texture
    }

    simRef.current.uniforms.uDelta.value =
      Math.min(delta, 0.033)

    const previousTarget = state.gl.getRenderTarget()
    const transition = attractorTransition.current

    transition.queued = attractor

    // Finish the active route before beginning another one. This keeps rapid
    // input continuous without needing pair-specific transition functions.
    if (
      transition.progress >= 1 &&
      transition.to !== transition.queued
    ) {
      transition.from = transition.to
      transition.to = transition.queued
      transition.progress = 0
    }

    transition.progress = Math.min(
      1,
      transition.progress + delta / Math.max(transitionDuration, 0.001)
    )

    simRef.current.uniforms.uFromAttractor.value =
      transition.from
    simRef.current.uniforms.uToAttractor.value =
      transition.to
    simRef.current.uniforms.uTransition.value =
      transition.progress

    state.gl.setRenderTarget(writeTarget)
    state.gl.clear()
    state.gl.render(scene, camera)
    state.gl.setRenderTarget(previousTarget)
    renderRef.current.uniforms.positions.value =
      writeTarget.texture

    renderRef.current.uniforms.uFocus.value =
      THREE.MathUtils.lerp(
        renderRef.current.uniforms.uFocus.value,
        focus,
        0.1
      )

    renderRef.current.uniforms.uFov.value =
      THREE.MathUtils.lerp(
        renderRef.current.uniforms.uFov.value,
        fov,
        0.1
      )

    renderRef.current.uniforms.uBlur.value =
      THREE.MathUtils.lerp(
        renderRef.current.uniforms.uBlur.value,
        (5.6 - aperture) * 1.5,
        0.1
      )

    simulationFrame.current += 1
  })

  return (
    <>
      {/* Simulation goes into a FBO/Off-buffer */}
      {createPortal(
        <mesh>
          <simulationMaterial ref={simRef} args={[size]} />
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3} />
            <bufferAttribute attach="attributes-uv" count={uvs.length / 2} array={uvs} itemSize={2} />
          </bufferGeometry>
        </mesh>,
        scene
      )}
      {/* The result of which is forwarded into a pointcloud via data-texture */}
      <points {...props}>
        <dofPointsMaterial ref={renderRef} />
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={particles.length / 3} array={particles} itemSize={3} />
        </bufferGeometry>
      </points>
    </>
  )
}
